import i18next from 'i18next'
import React from 'react'
import { datalayerHandler } from '../../../../../utils/datalayer'
import { Link } from '../../../../../utils/link'
import { ParsedPhoneNumber } from '../../../../../utils/phoneNumber'
import { locationExpandBlockClasses } from '../../../../../utils/style'
import { convertReactNodeToString, getClassName } from '../../../../../utils/utils'
import { useInView } from '../../../../hooks/useInView'
import { InviewOptions } from '../../blocksState'
import { ExpandBlockStyle } from '../expandBlock/expandBlockLayout'

export type LocationExpandBlockStyle = Partial<{
  locationsWrapperClassName: string
  locationListClassName: string
  locationItemClassName: string
  locationNameClassName: string
  locationPhoneClassName: string
  locationDescriptionClassName: string
  locationClickableClassName: string
  locationClickableWrapperClassName: string
  locationClickableLabelClassName: string
  locationClickableIconClassName: string
  locationClickableIconId: string
}> &
  ExpandBlockStyle

type LocationExpandBlockProps = {
  index: number
  inview?: InviewOptions
  title?: string
  intro?: string
  locations: {
    title: string
    url: string
    expanded: boolean
    clinics: {
      kind: 'Polikliniek' | 'Verpleegafdeling'
      title: string
      phoneNumber?: Array<ParsedPhoneNumber>
      openingTimes?: string
      visitingTimes?: string
      route?: string
    }[]
  }[]
  expand: (i: number) => void
}

export const LocationExpandableBlockLayout = (props: LocationExpandBlockProps) => {
  const { index, title, intro, inview, locations } = props
  const classes = locationExpandBlockClasses
  const { inView, ref } = useInView(
    { threshold: 0.1 },
    (inview) => console.debug("LocationExpandableBlockLayout inView:", inview),
    !inview?.triggerOnce
  )
  return (
    <section
      key={`block-${index}`}
      id={`io-${index}`}
      ref={ref}
      className={getClassName(classes, 'blockClassName', 'container') + ' ' + (inView ? getClassName(classes, 'inviewClassName', 'inview') : '')}
    >
      <div className={getClassName(locationExpandBlockClasses, 'innerClassName', 'py-5')}>
        {(title || intro) && (
          <header className={getClassName(locationExpandBlockClasses, 'headerClassName', '')}>
            {title && (
              <h2 className={getClassName(locationExpandBlockClasses, 'titleClassName', 'mb-3')}>
                <span id={`${index}`} aria-hidden="true" className="block__anchor" />
                {title}
              </h2>
            )}
            {intro && (
              <div
                className={getClassName(locationExpandBlockClasses, 'introClassName', '')}
                dangerouslySetInnerHTML={{ __html: intro }}
              />
            )}
          </header>
        )}

        {locations.length > 0 && (
          <div className={getClassName(locationExpandBlockClasses, 'contentClassName', '')}>
            <ul className={getClassName(locationExpandBlockClasses, 'listClassName', '')}>
              {locations.map((location, i) => (
                <li
                  key={`location-expand-${location.title}`}
                  className={getClassName(locationExpandBlockClasses, 'expandItemClassName', '')}
                >
                  <button
                    className={getClassName(
                      locationExpandBlockClasses,
                      'expandButtonClassName',
                      `card-header ${location.expanded ? 'col--open' : 'col--closed'} text-left border-0`
                    )}
                    aria-controls={`a11y-${index}-${i}`}
                    aria-expanded={location.expanded}
                    onClick={() => {
                      location.expanded ||
                        datalayerHandler('expand', location.title, 'Expandable', {
                          kind: 'button',
                          title: convertReactNodeToString(location.title),
                          label: location.title,
                        })
                      props.expand(i)
                    }}
                  >
                    {location.title && (
                      <span
                        className={getClassName(
                          locationExpandBlockClasses,
                          'expandTitleClassName',
                          'btn btn-link m-0 p-0'
                        )}
                      >
                        {location.title}
                      </span>
                    )}
                    <svg
                      className={getClassName(locationExpandBlockClasses, 'expandIconClassName', 'mr-3')}
                      aria-hidden="true"
                      width="24px"
                      height="24px"
                    >
                      <use
                        xlinkHref={`${
                          location.expanded
                            ? getClassName(locationExpandBlockClasses, 'iconOpenId', '#icon--chevron-up')
                            : getClassName(locationExpandBlockClasses, 'iconClosedId', '#icon--chevron-down')
                        }`}
                      />
                    </svg>
                  </button>

                  <div
                    id={`a11y-${index}-${i}`}
                    aria-hidden={!location.expanded}
                    className={getClassName(
                      locationExpandBlockClasses,
                      'expandContentClassName',
                      `card-body pb-1 ${location.expanded ? '' : 'd-none'} text-left border-0`
                    )}
                  >
                    <address className={getClassName(locationExpandBlockClasses, 'locationsWrapperClassName', '')}>
                      <ul className={getClassName(locationExpandBlockClasses, 'locationListClassName', '')}>
                        {location.clinics
                          .sort((a, b) => (a.kind < b.kind ? -1 : 1))
                          .map((clinic) => (
                            <li
                              key={clinic.title}
                              className={getClassName(locationExpandBlockClasses, 'locationItemClassName', '')}
                            >
                              <h3 className={getClassName(locationExpandBlockClasses, 'locationNameClassName', '')}>
                                {clinic.kind}
                              </h3>
                              {(clinic.phoneNumber ?? []).map((phoneNumber, i)=> {
                                if(phoneNumber.formatted && phoneNumber.uri) {
                                  return <>
                                      <a
                                        href={phoneNumber.uri}
                                        title={i18next.t('common:callOn', { location: location.title })}
                                        className={getClassName(locationExpandBlockClasses, 'locationPhoneClassName', '')}
                                      >
                                        <span className="sr-text">{i18next.t('common:phoneNumber')}</span>
                                        {phoneNumber.formatted}
                                      </a>
                                      {
                                        i < ((clinic.phoneNumber ?? []).length - 1) && " ; "
                                      }
                                  </>
                                }
                              })}
                              {clinic.route && (
                                <p
                                  className={getClassName(
                                    locationExpandBlockClasses,
                                    'locationDescriptionClassName',
                                    ''
                                  )}
                                >
                                  {`${i18next.t('common:route')}: ${clinic.route}`}
                                </p>
                              )}
                              {clinic.openingTimes && (
                                <p
                                  className={getClassName(
                                    locationExpandBlockClasses,
                                    'locationDescriptionClassName',
                                    ''
                                  )}
                                >
                                  {`${clinic.openingTimes}`}
                                </p>
                              )}
                              {clinic.visitingTimes && (
                                <p
                                  className={getClassName(
                                    locationExpandBlockClasses,
                                    'locationDescriptionClassName',
                                    ''
                                  )}
                                >
                                  {`${i18next.t('common:visitingTimes')}: ${clinic.visitingTimes}`}
                                </p>
                              )}
                            </li>
                          ))}
                      </ul>
                    </address>

                    <div
                      className={getClassName(
                        locationExpandBlockClasses,
                        'locationClickableWrapperClassName',
                        'd-inline-block'
                      )}
                    >
                      <Link
                        url={location.url}
                        className={getClassName(
                          locationExpandBlockClasses,
                          'locationClickableClassName',
                          'btn btn-dark d-inline-flex'
                        )}
                      >
                        <span
                          className={getClassName(locationExpandBlockClasses, 'locationClickableLabelClassName', '')}
                        >
                          {i18next.t('common:moreInformationAboutLocation', {
                            location: location.title,
                          })}
                        </span>
                        <svg
                          className={getClassName(locationExpandBlockClasses, 'locationClickableIconClassName', 'ml-3')}
                          aria-hidden="true"
                          width="20px"
                          height="20px"
                        >
                          <use
                            xlinkHref={getClassName(
                              locationExpandBlockClasses,
                              'locationClickableIconId',
                              '#icon--chevron-right-large'
                            )}
                          />
                        </svg>
                      </Link>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  )
}
